import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion as m, useScroll, useTransform } from "framer-motion";
import {
  Aurora,
  Button,
  Container,
  Counter,
  Magnetic,
  Marquee,
  Reveal,
  Section,
  SectionHeading,
  SplitText,
  Spotlight,
} from "../components/ui";
import ProjectCard from "../components/ProjectCard";
import ArticleCard from "../components/ArticleCard";
import ProjectDetails from "../components/ProjectDetails";
import { featuredProjects, projects } from "../data/projects";
import { articles } from "../data/articles";
import { marqueeWords, principles, services, site, stats } from "../data/site";

const Home = () => {
  const [detail, setDetail] = useState(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const recentArticles = articles.slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{site.shortName} — {site.role}</title>
        <meta
          name="description"
          content={`${site.intro} Selected work, writing and capabilities.`}
        />
      </Helmet>

      {/* ───────────────────────── Hero ───────────────────────── */}
      <section
        ref={heroRef}
        className="relative flex min-h-screen items-center overflow-hidden bg-paper-warm pb-20 pt-36 dark:bg-night md:pt-40"
      >
        <Aurora tone="hero" />

        {/* Faint engineering grid, anchored to the gutter rhythm. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.5] dark:opacity-[0.28]"
          style={{
            backgroundImage:
              "linear-gradient(var(--color-border) 1px, transparent 1px), linear-gradient(90deg, var(--color-border) 1px, transparent 1px)",
            backgroundSize: "88px 88px",
            maskImage:
              "radial-gradient(ellipse 85% 65% at 50% 40%, #000 20%, transparent 78%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 85% 65% at 50% 40%, #000 20%, transparent 78%)",
          }}
        />

        <Container className="relative">
          <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
            <m.div style={{ y: titleY }} className="lg:col-span-7">
              <Reveal>
                <span className="t-eyebrow inline-flex items-center gap-3">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-electric opacity-70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-electric" />
                  </span>
                  {site.availability}
                </span>
              </Reveal>

              <SplitText
                as="h1"
                text="Systems that hold *under load.*"
                className="t-display mt-8"
                stagger={0.055}
              />
              <SplitText
                as="p"
                text="built end-to-end."
                className="t-display text-stroke"
                stagger={0.04}
              />

              <Reveal delay={0.35}>
                <p className="t-lead mt-9 max-w-prose">{site.intro}</p>
              </Reveal>

              <Reveal delay={0.45}>
                <div className="mt-11 flex flex-wrap items-center gap-5">
                  <Magnetic>
                    <Button to="/projects" size="lg" withArrow>
                      See the work
                    </Button>
                  </Magnetic>
                  <Button to="/blog" variant="ghost" withArrow>
                    Read the writing
                  </Button>
                </div>
              </Reveal>
            </m.div>

            <m.div style={{ y: portraitY }} className="lg:col-span-5">
              <Reveal direction="left" delay={0.2}>
                <div className="relative mx-auto max-w-md">
                  <div className="absolute -inset-3 rounded-lg border border-[var(--color-border)]" />
                  <Spotlight
                    radius={460}
                    intensity={0.10}
                    className="relative overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] shadow-xl"
                  >
                    <div className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] bg-gradient-brand px-6 py-5 text-paper">
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em]">
                        {site.role}
                      </p>
                      <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em]">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-paper/70" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-paper" />
                        </span>
                        Available
                      </span>
                    </div>

                    <div className="grid grid-cols-2 divide-x divide-y divide-[var(--color-border)]">
                      {stats.map((s) => (
                        <div key={s.label} className="px-6 py-6">
                          <p className="font-display text-4xl font-semibold leading-none text-azure dark:text-electric">
                            <Counter value={s.value} suffix={s.suffix} />
                          </p>
                          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted dark:text-slightwhite/50">
                            {s.label}
                          </p>
                        </div>
                      ))}
                    </div>

                    <ul className="divide-y divide-[var(--color-border)] border-t border-[var(--color-border)]">
                      {services.map((s) => (
                        <li
                          key={s.title}
                          className="flex items-baseline gap-4 px-6 py-3.5"
                        >
                          <span className="font-mono text-[10px] text-electric">
                            {String(s.index).padStart(2, "0")}
                          </span>
                          <span className="text-sm text-ink dark:text-slightwhite">
                            {s.title}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Spotlight>
                </div>
              </Reveal>
            </m.div>
          </div>
        </Container>

        <div className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 lg:block">
          <m.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-ink-muted dark:text-slightwhite/45"
          >
            Scroll
            <span className="h-10 w-px bg-[var(--color-border-strong)]" />
          </m.div>
        </div>
      </section>

      {/* ───────────────────── Discipline marquee ───────────────────── */}
      <Marquee
        items={marqueeWords}
        className="border-y border-[var(--color-border)] bg-[var(--color-bg)] py-6"
        itemClassName="font-display text-3xl md:text-5xl text-ink dark:text-slightwhite"
      />

      {/* ───────────────────────── Selected work ───────────────────────── */}
      <Section tone="warm" id="work">
        <SectionHeading
          eyebrow="Selected work"
          index={1}
          title="Projects with a *problem* behind them."
          lead="Each of these starts with something that was genuinely broken. The screenshots come last."
          action={
            <Button to="/projects" variant="outline" withArrow>
              All {projects.length} projects
            </Button>
          }
        />

        <div className="mt-16 grid gap-x-8 gap-y-14 md:grid-cols-2">
          {featuredProjects.slice(0, 6).map((project, i) => (
            <ProjectCard
              key={project.slug}
              project={project}
              index={i}
              size="feature"
              onOpen={setDetail}
            />
          ))}
        </div>
      </Section>

      {/* ───────────────────────── Capabilities ───────────────────────── */}
      <Section tone="default">
        <SectionHeading
          eyebrow="Capabilities"
          index={2}
          title="Four practices, one *method.*"
          lead="Different outputs, same approach: understand the constraint, make the decision legible, build the thing that survives contact with reality."
        />

        <div className="mt-16">
          {services.map((service, i) => (
            <Reveal key={service.title} delay={i * 0.06}>
              <div className="group grid gap-5 border-t border-[var(--color-border)] py-10 transition-colors duration-500 hover:border-azure dark:hover:border-electric lg:grid-cols-12 lg:gap-10">
                <span className="font-mono text-[11px] tracking-[0.2em] text-electric lg:col-span-1">
                  {String(service.index).padStart(2, "0")}
                </span>
                <h3 className="font-display text-3xl leading-tight transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric lg:col-span-4 lg:text-[2.25rem]">
                  {service.title}
                </h3>
                <div className="lg:col-span-5">
                  <p className="text-[15px] leading-relaxed text-ink-muted dark:text-slightwhite/70">
                    {service.summary}
                  </p>
                </div>
                <ul className="space-y-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted dark:text-slightwhite/55 lg:col-span-2">
                  {service.capabilities.slice(0, 4).map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
          <div className="rule" />
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12">
            <Button to="/skills" variant="outline" withArrow>
              The full toolkit
            </Button>
          </div>
        </Reveal>
      </Section>

      {/* ───────────────────────── Principles ───────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-brand py-24 text-paper md:py-36">
        <div className="noise-panel pointer-events-none absolute inset-0 opacity-[0.08]" />
        <Container className="relative">
          <Reveal>
            <span className="t-eyebrow text-electric-soft">
              <span className="mr-3 inline-block h-px w-8 bg-electric-soft align-middle" />
              03 — How I work
            </span>
          </Reveal>
          <SplitText
            as="h2"
            text="Four rules I do not negotiate on."
            className="t-title mt-7 max-w-3xl"
          />

          <div className="mt-16 grid gap-10 md:grid-cols-2 lg:gap-14">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-7">
                  <h3 className="font-display text-2xl text-electric md:text-[1.75rem]">
                    {p.title}
                  </h3>
                  <p className="mt-4 max-w-md text-[15px] leading-[1.8] text-paper/75">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ───────────────────────── Writing ───────────────────────── */}
      <Section tone="default">
        <SectionHeading
          eyebrow="Recent writing"
          index={4}
          title="Notes on building things *properly.*"
          lead="Essays on engineering practice — pipelines, architecture, debugging, and the parts of the craft nobody documents."
          action={
            <Button to="/blog" variant="outline" withArrow>
              All writing
            </Button>
          }
        />

        <div className="mt-16 grid gap-x-8 gap-y-14 md:grid-cols-3">
          {recentArticles.map((article, i) => (
            <ArticleCard key={article.slug} article={article} index={i} />
          ))}
        </div>
      </Section>

      {/* ───────────────────────── Contact prompt ───────────────────────── */}
      <Section tone="warm" className="!py-20 md:!py-24">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <p className="t-eyebrow">Next</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl leading-tight md:text-5xl">
              Have something that needs building?
            </h2>
          </div>
          <Magnetic>
            <Button to="/contact" size="lg" withArrow>
              Start a conversation
            </Button>
          </Magnetic>
        </div>
      </Section>

      <ProjectDetails detail={detail} setView={() => setDetail(null)} />

      <Link to="/profile" className="sr-only">
        About the studio
      </Link>
    </>
  );
};

export default Home;
